import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { TextField } from '@fluentui/react/lib/TextField';
import { useConversion, useSubstitutionPicker, hasSubstitutions } from 'src/components/base/fields/utils';
import { SubstitutionPicker, classNames } from 'src/components/base/fields/SubstitutionPicker';
import { asString } from 'shared/utils/utils';


export const Type = {
  Int: 'int',
  Double: 'double',
  String: 'string',
};

export const Errors = {
  Int: 'Invalid integer value',
  Double: 'Invalid double value',
};


export class Variant {
  _valueStr = '';
  _type = Type.String;

  constructor(value, type = Type.String) {
    this._valueStr = asString(value);
    this._type = type;
  }

  type() { return this._type; }

  asString() { return this._valueStr; }
  asInt() { return this._valueStr ? +this._valueStr : Number.NaN; }
  asDouble() { return this._valueStr ? +this._valueStr : Number.NaN; }

  isValid() { 
    switch (this._type) {
      case Type.Double: return !isNaN(this.asDouble());
      case Type.Int: return Number.isInteger(this.asInt());
      case Type.String: return true;
    }
  }

  asType() {
    switch (this._type) {
      case Type.Double: return this.asDouble();
      case Type.String: return this.asString();
      case Type.Int: return this.asInt();
    }
  }

  getError() {
    return this.isValid()
      ? ''
      : this._type === Type.Int ? Errors.Int : Errors.Double; 
  }
};


export const TextConverters = {
  parse: (text, type) => {  
    const value = new Variant(text, type);
    return { value, error: value.getError() };
  },
  format: (variant, parseSubstitution) => {
    const s = variant.asString();
    const { substitution } = parseSubstitution(s);
    return { value: substitution ?? s, error: substitution ? '' : variant.getError(), substitution };
  },
  getDefault: (type) => new Variant(type !== Type.String ? '0' : '', type),
};


export function GenericField({ converters, ...props }) {
  const { type } = props;

  const c = mergeConverters(converters, type);
  const cvt = useConversion(props, c);
  const sProps = useSubstitutionPicker(props);

  return (
    <div className={classNames.withTargetPicker}>
      <GenericFieldBase {...props} {...cvt}/> 
      <SubstitutionPicker {...sProps}/>
    </div>
  );
}



export function GenericFieldBase({ value: initialValue /* Variant */, substitution, error, disabled, onChange, textFieldProps = {}}) {
  // We need to deal with numbers as well, but in some cases
  // . or - signs are lost in a string representation when converted to number
  // so that we cannot display them in the TextField
  const [valueString, setValueString] = useState(initialValue.asString());

  useEffect(() => updateStringValue(), []);
  useEffect(() => updateStringValue(), [substitution, initialValue]);
  
  function updateStringValue() {
    if (!substitution && initialValue.isValid()) {
      setValueString(initialValue.asString());
    }
  }

  function handleChange(event, value) {
    setValueString(value);
    // onChange(value, validate(value));
    onChange(new Variant(value, initialValue.type()));
  }

  return (
    <TextField 
      disabled={disabled}
      value={substitution ?? valueString} onChange={handleChange} errorMessage={error}
      styles={{ root: { width: '100%' } }}
      {...textFieldProps} 
    />
  );
}


function mergeConverters(converters, type) {
  const { 
    parse = (text, parseSubstitution) => TextConverters.parse(text, type), 
    format = (value, parseSubstitution) => TextConverters.format(value, parseSubstitution),
    getDefault = () => TextConverters.getDefault(type),
  } = converters ?? {};
  return { parse, format, getDefault };
}



export default GenericField;